import { getService } from "@hoppscotch/common/modules/dioc"
import { OrganizationPlatformDef } from "@hoppscotch/common/platform/organization"
import { markRaw } from "vue"

import { isDefaultCloudInstance } from "@app/helpers/utils/cloud-for-orgs"
import { OrganizationService } from "@app/services/organization.service"
import OrganizationSidebar from "@app/components/organization/Sidebar.vue"
import { ROOT_DOMAIN_PATTERN } from "@app/helpers/validation/domain-patterns"

export const def: OrganizationPlatformDef = {
  isDefaultCloudInstance,
  getOrgInfo: async () => {
    const organizationService = getService(OrganizationService)

    const orgInfo = await organizationService.getOrganizationInfo()

    if (!orgInfo) {
      return null
    }

    const { orgID, orgDomain, orgName, orgLogo, error } = orgInfo

    // Check if the user is an admin of the organization (if the user is not an admin, the error will be 'organization/organization_member_not_admin')
    return {
      orgID,
      orgDomain,
      name: orgName,
      logo: orgLogo || null,
      isAdmin: !error,
    }
  },
  getRootDomain: () => {
    const hostname = window.location.hostname

    // Production
    if (hostname === "hoppscotch.io") {
      return "hoppscotch.io"
    }

    // Local development with custom org root domain support
    if (hostname === "localhost") {
      const orgRootDomain = import.meta.env.VITE_ORG_ROOT_DOMAIN
      // Validate VITE_ORG_ROOT_DOMAIN if provided
      if (orgRootDomain && ROOT_DOMAIN_PATTERN.test(orgRootDomain)) {
        return orgRootDomain
      }
      // Fall back to hoppscotch.io if not set or invalid
      return "hoppscotch.io"
    }

    // Default cloud instance (local development simulation)
    if (hostname === "default.hoppscotch.io") {
      return "default.hoppscotch.io"
    }

    // Cloud staging
    if (
      hostname === "cloud-staging.deploychan.hoppscotch.com" ||
      hostname.endsWith(".cloud-staging.deploychan.hoppscotch.com")
    ) {
      return "cloud-staging.deploychan.hoppscotch.com"
    }

    // Cloud orgs staging
    if (
      hostname === "cloud-orgs-staging.deploychan.hoppscotch.com" ||
      hostname.endsWith(".cloud-orgs-staging.deploychan.hoppscotch.com")
    ) {
      return "cloud-orgs-staging.deploychan.hoppscotch.com"
    }

    // Org subdomain on any other root — derive root from hostname
    // e.g. `orgs.hoppscotch.io` → `hoppscotch.io`
    return hostname.split(".").slice(1).join(".")
  },
  initiateOnboarding: () => {
    if (isDefaultCloudInstance) {
      // Already on default instance, navigate directly
      window.location.href = "/orgs"
    } else {
      // On an org subdomain — redirect to the default instance /orgs route
      const rootDomain = def.getRootDomain()
      window.location.href = `${window.location.protocol}//${rootDomain}/orgs`
    }
  },
  customOrganizationSwitcherComponent: markRaw(OrganizationSidebar),
}
